// MasterDetailView.jsx — Reusable Master-Detail Timeline & History View for Reva Jewellers
import React from "react";
import "../pages/common.css";

/**
 * Reusable Master-Detail View Component
 *
 * @param {string}   title               Main header title
 * @param {string}   subtitle            Subtitle / description
 * @param {string}   backLabel           Back button text (default: "Back")
 * @param {Function} onBack              Callback when back button is clicked
 * @param {string}   badgeText           Header counter badge (e.g. "3 versions recorded")
 * @param {string}   timelineTitle       Left panel title (default: "Timeline")
 * @param {Array}    items               Array of list/timeline items
 * @param {any}      selectedItem        Currently active item
 * @param {Function} onSelectItem        Callback when an item in the left panel is clicked
 * @param {Function} renderTimelineItem  Render prop for each item in the left timeline: (item, index, isSelected) => JSX
 * @param {Function} renderDetail        Render prop for the right detail panel: (selectedItem) => JSX
 * @param {boolean}  loading             Loading state indicator
 * @param {string}   emptyTitle          Title when items array is empty
 * @param {string}   emptyMessage        Message when items array is empty
 * @param {JSX}      extraHeaderActions  Optional extra buttons in the top header
 */
export default function MasterDetailView({
  title,
  subtitle,
  backLabel = "Back",
  onBack,
  badgeText,
  timelineTitle = "Timeline",
  items = [],
  selectedItem,
  onSelectItem,
  renderTimelineItem,
  renderDetail,
  loading = false,
  emptyTitle = "No Entries Found",
  emptyMessage = "No history or revisions recorded yet.",
  extraHeaderActions = null,
}) {
  React.useEffect(() => {
    const el = document.querySelector(".rv-content");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);

  const handleItemClick = (item) => {
    if (onSelectItem) onSelectItem(item);
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setTimeout(() => {
        const detailEl = document.querySelector(".rv-md-detail-panel");
        if (detailEl) {
          detailEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
    }
  };

  return (
    <div className="rv-inv-page" style={{ gap: 16 }}>
      {/* Top Navigation Bar */}
      <div className="rv-inv-topbar">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {onBack && (
            <button
              className="rv-btn rv-btn--ghost"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", fontWeight: 600 }}
              onClick={onBack}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {backLabel}
            </button>
          )}
          <div>
            <h2 className="rv-inv-heading" style={{ margin: 0, fontSize: 18 }}>
              {title}
            </h2>
            {subtitle && (
              <p style={{ margin: 0, fontSize: 12, color: "var(--taupe)" }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {badgeText && <span className="rv-inv-count">{badgeText}</span>}
          {extraHeaderActions}
        </div>
      </div>

      {loading ? (
        <div className="rv-card" style={{ padding: 60, textAlign: "center" }}>
          <div className="rv-loader" />
          <p style={{ marginTop: 12, color: "var(--taupe)" }}>Loading details…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="rv-card rv-inv-empty">
          <div className="rv-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <p className="rv-empty-title">{emptyTitle}</p>
          <p className="rv-empty-sub">{emptyMessage}</p>
          {onBack && (
            <button className="rv-btn rv-btn--primary rv-mt-12" onClick={onBack}>
              {backLabel}
            </button>
          )}
        </div>
      ) : (
        /* Master-Detail Split Grid */
        <div className="rv-md-layout">
          {/* Left Panel: Timeline List */}
          <div className="rv-card rv-custom-scroll rv-md-timeline-panel">
            <div className="rv-md-timeline-header">
              <span>{timelineTitle}</span>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 600 }}>
                {items.length} {items.length === 1 ? "entry" : "entries"}
              </span>
            </div>

            <div className="rv-md-timeline-items">
              {items.map((item, idx) => {
                const isSelected = selectedItem != null && item === selectedItem;
                return (
                  <div
                    key={item.id ?? item.key ?? item.receiptLineID ?? item.priceID ?? item.noteHistoryID ?? idx}
                    onClick={() => handleItemClick(item)}
                    style={{
                      padding: "12px 14px",
                      borderRadius: 10,
                      background: isSelected ? "#FAF4EF" : "#ffffff",
                      border: `1.5px solid ${isSelected ? "var(--taupe, #B9A792)" : "rgba(185,167,146,.25)"}`,
                      boxShadow: isSelected ? "0 3px 10px rgba(185,167,146,.25)" : "0 1px 3px rgba(0,0,0,.02)",
                      cursor: "pointer",
                      transition: "all .18s ease",
                    }}
                  >
                    {renderTimelineItem ? renderTimelineItem(item, idx, isSelected) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--dark)" }}>Entry #{idx + 1}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Full Detail Inspector */}
          <div className="rv-card rv-custom-scroll rv-md-detail-panel">
            {selectedItem ? (
              renderDetail ? renderDetail(selectedItem) : (
                <div>No detail renderer provided.</div>
              )
            ) : (
              <div style={{ textAlign: "center", padding: "60px 0", color: "var(--taupe)" }}>
                Select an item on the left to inspect full details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
